import { initDb, getZooId } from "../tools/conf";
import { v4 as uuidv4 } from "uuid";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { hasUnreadReplyForUser } from "../components/Views/HelpCenter/utils/ticketReadState";

/**
 * Vrai quand la mise à jour change le statut et que c'est le demandeur du
 * ticket qui la fait (GitLab #1161).
 * @param {Object} updates
 * @param {{ actorId?: string|null, requesterId?: string|null }} people
 * @returns {boolean}
 */
export function isRequesterStatusChange(updates, { actorId = null, requesterId = null } = {}) {
  return Boolean(
    updates &&
      Object.prototype.hasOwnProperty.call(updates, "status") &&
      actorId &&
      requesterId &&
      actorId === requesterId
  );
}

/**
 * Connecteur Firestore pour les tickets de support
 * Collection: helpTickets
 *
 * @class TicketDatabaseConnector
 */
export class TicketDatabaseConnector {
  constructor() {
    this.collectionName = "helpTickets";
  }

  /**
   * Récupère la référence à la collection
   * @private
   * @returns {firebase.firestore.CollectionReference}
   */
  _getCollection() {
    const db = initDb();
    return db.collection(this.collectionName);
  }

  /**
   * Crée un nouveau ticket de support
   * @param {Object} ticketData
   * @param {string} ticketData.title
   * @param {string} ticketData.description
   * @param {string} ticketData.category - bug|feature_request|question|other
   * @param {string} ticketData.priority - low|medium|high|critical
   * @param {string} [ticketData.userId]
   * @param {string} [ticketData.userName]
   * @param {string} [ticketData.userEmail]
   * @param {string} [ticketData.zooId]
   * @param {Array} [ticketData.attachments]
   * @returns {Promise<Object>}
   */
  async createTicket(ticketData) {
    const collection = this._getCollection();
    const ticketId = `ticket_${uuidv4()}`;
    const zooId = ticketData.zooId || (await getZooId());

    const ticket = {
      ticketId,
      zooId,
      zooName: ticketData.zooName || null,
      userId: ticketData.userId || null,
      userName: ticketData.userName || "Unknown",
      userEmail: ticketData.userEmail || null,
      title: ticketData.title || "",
      description: ticketData.description || "",
      category: ticketData.category || "other",
      priority: ticketData.priority || "medium",
      status: "open",
      attachments: ticketData.attachments || [],
      messages: [],
      slackMessageTs: null,
      slackChannelId: null,
      createdAt: firebase.firestore.Timestamp.now(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      resolvedAt: null,
    };

    // Remove undefined values
    Object.keys(ticket).forEach((key) => {
      if (ticket[key] === undefined) delete ticket[key];
    });

    try {
      await collection.doc(ticketId).set(ticket);
      console.log("[TicketConnector] Created ticket:", ticketId);
      return { id: ticketId, ...ticket };
    } catch (error) {
      console.error("[TicketConnector] createTicket error:", error);
      throw error;
    }
  }

  /**
   * Récupère les tickets d'un zoo
   * @param {string} zooId
   * @param {Object} [options]
   * @param {string} [options.status] - Filtrer par statut
   * @param {number} [options.limit]
   * @returns {Promise<Array>}
   */
  async getTicketsByZoo(zooId, options = {}) {
    try {
      let query = this._getCollection().where("zooId", "==", zooId);

      if (options.status) {
        query = query.where("status", "==", options.status);
      }

      query = query.orderBy("createdAt", "desc");

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const snapshot = await query.get();
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("[TicketConnector] getTicketsByZoo error:", error);
      throw error;
    }
  }

  /**
   * Récupère les tickets d'un utilisateur
   * @param {string} userId
   * @param {Object} [options]
   * @param {string} [options.status]
   * @param {number} [options.limit]
   * @returns {Promise<Array>}
   */
  async getTicketsByUser(userId, options = {}) {
    try {
      let query = this._getCollection().where("userId", "==", userId);

      if (options.status) {
        query = query.where("status", "==", options.status);
      }

      query = query.orderBy("createdAt", "desc");

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const snapshot = await query.get();
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("[TicketConnector] getTicketsByUser error:", error);
      throw error;
    }
  }

  /**
   * Met à jour un ticket
   *
   * Quand le demandeur change lui-même le statut (actorId === requesterId),
   * `requesterLastReadAt` est écrit dans la même opération : la Function
   * `handleHelpTicketUpdate` y lit que le changement vient du demandeur et ne
   * lui renvoie pas l'e-mail « Votre ticket est … » (GitLab #1161). Ce champ
   * est déjà autorisé au demandeur par les règles : aucune règle ne change.
   * Le support (actorId différent ou absent) n'écrit jamais ce champ.
   *
   * @param {string} ticketId
   * @param {Object} updates
   * @param {Object} [options]
   * @param {string} [options.actorId] - uid de la personne qui fait la mise à jour
   * @param {string} [options.requesterId] - `userId` du ticket (demandeur)
   * @returns {Promise<void>}
   */
  async updateTicket(ticketId, updates, { actorId = null, requesterId = null } = {}) {
    try {
      const updateData = { ...updates };
      updateData.updatedAt = firebase.firestore.FieldValue.serverTimestamp();

      if (isRequesterStatusChange(updates, { actorId, requesterId })) {
        updateData.requesterLastReadAt = firebase.firestore.Timestamp.now();
      }

      // Track resolved timestamp
      if (updates.status === "resolved" || updates.status === "closed") {
        updateData.resolvedAt = firebase.firestore.FieldValue.serverTimestamp();
      }

      // Remove undefined values
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) delete updateData[key];
      });

      await this._getCollection().doc(ticketId).update(updateData);
      console.log("[TicketConnector] Updated ticket:", ticketId);
    } catch (error) {
      console.error("[TicketConnector] updateTicket error:", error);
      throw error;
    }
  }

  /**
   * Ajoute un message à un ticket
   * @param {string} ticketId
   * @param {Object} message
   * @param {string} message.authorId
   * @param {string} message.authorName
   * @param {string} message.content
   * @param {string} [message.source] - app|slack|jira
   * @returns {Promise<Object>}
   */
  async addMessage(ticketId, message) {
    const messageObj = {
      messageId: `msg_${uuidv4()}`,
      authorId: message.authorId || null,
      authorName: message.authorName || "Unknown",
      content: message.content || "",
      source: message.source || "app",
      createdAt: firebase.firestore.Timestamp.now(),
    };

    if (message.imageUrl) {
      messageObj.imageUrl = message.imageUrl;
      messageObj.imageName = message.imageName || "image";
    }

    // Remove undefined values
    Object.keys(messageObj).forEach((key) => {
      if (messageObj[key] === undefined) delete messageObj[key];
    });

    try {
      await this._getCollection().doc(ticketId).update({
        messages: firebase.firestore.FieldValue.arrayUnion(messageObj),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      console.log("[TicketConnector] Added message to ticket:", ticketId);
      return messageObj;
    } catch (error) {
      console.error("[TicketConnector] addMessage error:", error);
      throw error;
    }
  }

  /**
   * Marque le ticket comme lu par le demandeur.
   * Ne touche pas updatedAt pour ne pas réordonner les listes.
   * @param {string} ticketId
   * @returns {Promise<void>}
   */
  async markTicketReadByRequester(ticketId) {
    try {
      await this._getCollection().doc(ticketId).update({
        requesterLastReadAt: firebase.firestore.Timestamp.now(),
      });
    } catch (error) {
      console.error("[TicketConnector] markTicketReadByRequester error:", error);
    }
  }

  /**
   * S'abonne aux tickets d'un zoo (temps réel)
   * @param {string} zooId
   * @param {Function} callback - (tickets[]) => void
   * @returns {Function} unsubscribe
   */
  subscribeToTickets(zooId, callback) {
    const query = this._getCollection()
      .where("zooId", "==", zooId)
      .orderBy("createdAt", "desc");

    return query.onSnapshot(
      (snapshot) => {
        const tickets = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(tickets);
      },
      (error) => {
        console.error("[TicketConnector] subscribeToTickets error:", error);
      }
    );
  }

  /**
   * S'abonne aux changements d'un ticket (temps réel)
   * @param {string} ticketId
   * @param {Function} callback - (ticket) => void
   * @returns {Function} unsubscribe
   */
  subscribeToTicket(ticketId, callback) {
    return this._getCollection().doc(ticketId).onSnapshot(
      (doc) => {
        if (doc.exists) {
          callback({ id: doc.id, ...doc.data() });
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error("[TicketConnector] subscribeToTicket error:", error);
      }
    );
  }

  /**
   * S'abonne à tous les tickets (admin multi-site, temps réel)
   * @param {Function} callback - (tickets[]) => void
   * @param {Function} [onError] - (error) => void, appelé si l’abonnement échoue
   * @returns {Function} unsubscribe
   */
  subscribeToAllTickets(callback, onError) {
    const query = this._getCollection().orderBy("createdAt", "desc");

    return query.onSnapshot(
      (snapshot) => {
        const tickets = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(tickets);
      },
      (error) => {
        console.error("[TicketConnector] subscribeToAllTickets error:", error);
        if (typeof onError === "function") onError(error);
      }
    );
  }

  /**
   * S'abonne au nombre de tickets dont l'utilisateur est le demandeur et qui
   * portent une réponse qu'il n'a pas encore lue (badge sidebar, temps réel).
   *
   * Le calcul est fait côté client : « non lu » compare `requesterLastReadAt`
   * à la date du dernier message dont l'auteur n'est pas le demandeur, ce qui
   * n'est pas exprimable en requête Firestore.
   *
   * @param {string} userId
   * @param {Function} callback - (count) => void
   * @returns {Function} unsubscribe
   */
  subscribeToUnreadRepliesCount(userId, callback) {
    if (!userId) return () => {};

    const query = this._getCollection().where("userId", "==", userId);

    return query.onSnapshot(
      (snapshot) => {
        const count = snapshot.docs.filter((doc) =>
          hasUnreadReplyForUser({ id: doc.id, ...doc.data() }, userId)
        ).length;
        callback(count);
      },
      (error) => {
        console.error("[TicketConnector] subscribeToUnreadRepliesCount error:", error);
      }
    );
  }

  /**
   * S'abonne au compteur de tickets ouverts (admin badge, temps réel)
   * @param {Function} callback - (count) => void
   * @returns {Function} unsubscribe
   */
  subscribeToOpenTicketsCount(callback) {
    const query = this._getCollection()
      .where("status", "in", ["open", "in_progress"]);

    return query.onSnapshot(
      (snapshot) => {
        // Les demandes d'évolution ne sont pas du support à traiter (même
        // règle que la file des tickets V2, `isEvolutionTicket`).
        callback(snapshot.docs.filter((doc) => doc.data().category !== "feature_request").length);
      },
      (error) => {
        console.error("[TicketConnector] subscribeToOpenTicketsCount error:", error);
      }
    );
  }
}

// Singleton instance
let instance = null;

/**
 * Récupère l'instance singleton du connecteur
 * @returns {TicketDatabaseConnector}
 */
export const getTicketConnector = () => {
  if (!instance) {
    instance = new TicketDatabaseConnector();
  }
  return instance;
};

export default TicketDatabaseConnector;
